import React from "react";
import { Phone, MessageCircle, MapPin, User, Navigation, Clock } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { t, BRANCHES } from "../lib/i18n";

const BranchesPage = () => {
  const { lang, tr } = useLang();
  return (
    <div data-testid="branches-page" className="max-w-7xl mx-auto px-4 lg:px-8 py-12 lg:py-16">
      <div className="max-w-3xl">
        <div className="text-xs uppercase tracking-[0.14em] text-teal-700 font-semibold">{tr(t.nav.branches)}</div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-heading font-semibold text-slate-900">{tr(t.branches.title)}</h1>
        <p className="mt-2 text-slate-600">{tr(t.branches.subtitle)}</p>
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {BRANCHES.map((b) => (
          <div key={b.key} className="rounded-xl bg-white border border-slate-200 overflow-hidden" data-testid={`branch-${b.key}`}>
            <a
              href={b.mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="relative block group"
              aria-label={tr({ en: "Open location in Google Maps", hi: "गूगल मैप्स में स्थान खोलें" })}
              data-testid={`branch-map-link-${b.key}`}
            >
              <iframe
                title={b.key}
                src={b.mapEmbed}
                className="w-full h-56 border-0 pointer-events-none"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
              <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/10 transition-colors" />
              <div className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/95 backdrop-blur border border-slate-200 text-xs font-medium text-slate-800 shadow-sm">
                <Navigation className="w-3.5 h-3.5 text-teal-700" />
                {tr({ en: "Open in Google Maps", hi: "गूगल मैप्स में खोलें" })}
              </div>
            </a>
            <div className="p-6">
              <h3 className="font-heading text-xl font-semibold text-slate-900">
                {lang === "hi" ? b.name_hi : b.name_en}
              </h3>

              <div className="mt-3 space-y-2 text-sm text-slate-700">
                <div className="flex items-start gap-2">
                  <User className="w-4 h-4 mt-0.5 text-teal-700 shrink-0" />
                  <span>
                    <span className="text-slate-500">{tr(t.branches.operator)}: </span>
                    <span className="font-medium text-slate-900">{lang === "hi" ? b.operator_hi : b.operator_en}</span>
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 text-teal-700 shrink-0" />
                  <span>{lang === "hi" ? b.address_hi : b.address_en}</span>
                </div>
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 mt-0.5 text-teal-700 shrink-0" />
                  <span className="flex flex-col">
                    <span>{lang === "hi" ? b.hours_hi : b.hours_en}</span>
                    <span className="text-slate-500">{lang === "hi" ? b.hours_sunday_hi : b.hours_sunday_en}</span>
                  </span>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-3">
                <a
                  href={b.mapUrl}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-teal-700 hover:bg-teal-800 text-white text-sm font-medium transition-colors"
                  data-testid={`branch-directions-${b.key}`}
                >
                  <Navigation className="w-4 h-4" /> {tr({ en: "Get Directions", hi: "रास्ता देखें" })}
                </a>
                <a
                  href={`tel:${b.mobile}`}
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-slate-300 text-slate-800 hover:bg-slate-50 text-sm font-medium transition-colors"
                  data-testid={`branch-call-${b.key}`}
                >
                  <Phone className="w-4 h-4" /> {b.mobile}
                </a>
                <a
                  href={`https://wa.me/${b.whatsapp}?text=${encodeURIComponent("Namaste " + (lang === "hi" ? b.name_hi : b.name_en) + " Ashish Point!")}`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-green-600 hover:bg-green-700 text-white text-sm font-medium transition-colors"
                  data-testid={`branch-whatsapp-${b.key}`}
                >
                  <MessageCircle className="w-4 h-4" /> {tr(t.common.whatsapp)}
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BranchesPage;
